import { Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MinistryIcon from "../components/MinistryIcon";
import Modal from "../components/Modal";
import Spinner from "../components/Spinner";
import VolunteerCalendar from "../components/VolunteerCalendar";
import { useVolunteer } from "../lib/useVolunteer";
import { dedupeRoles } from "../lib/role-label";
import { payloadTimeZone } from "../lib/timezone";

export default function VolunteerModal() {
  const { volunteerId } = useParams();
  const navigate = useNavigate();
  const { data, error, loading } = useVolunteer(volunteerId);

  const close = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  return (
    <Modal open onClose={close} ariaLabel="Volunteer history">
      {loading ? (
        <Spinner label="Loading volunteer" />
      ) : error || !data ? (
        <p className="placeholder">Couldn't load this volunteer{error ? `: ${error}` : ""}.</p>
      ) : (
        <VolunteerBody data={data} />
      )}
    </Modal>
  );
}

function VolunteerBody({ data }: { data: NonNullable<ReturnType<typeof useVolunteer>["data"]> }) {
  const { volunteer, history } = data;
  const timeZone = payloadTimeZone(data.timezone);
  const name = `${volunteer.firstName} ${volunteer.lastName}`;
  const lastLoginPhrase = volunteer.lastLoginAt
    ? `Last login ${friendlyDate(volunteer.lastLoginAt, timeZone)}`
    : "Has never logged in";

  return (
    <>
      <header className="volunteer__header">
        <h2 className="volunteer__name">{name}</h2>
        <p className="volunteer__contact">
          {volunteer.email ? <span>{volunteer.email}</span> : <span className="volunteer__missing">no email on file</span>}
          {volunteer.phone ? <span> · {volunteer.phone}</span> : null}
        </p>
        <p className="volunteer__meta">
          {(() => {
            const roles = dedupeRoles(volunteer.ministries);
            return roles.length > 0 ? (
              roles.map((role, i) => (
                <Fragment key={role}>
                  <span className="volunteer__ministry">
                    <MinistryIcon ministry={role} />
                    <span>{role}</span>
                  </span>
                  {i < roles.length - 1 ? <span aria-hidden="true"> · </span> : null}
                </Fragment>
              ))
            ) : (
              <span>No ministry qualifications</span>
            );
          })()}
          <span aria-hidden="true"> · </span>
          {lastLoginPhrase}
        </p>
      </header>

      <VolunteerCalendar history={history} timeZone={timeZone} />
    </>
  );
}

// lastLoginAt is an instant — render its parish-local calendar date
// so it matches every other date on the page.
function friendlyDate(iso: string, timeZone: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  });
}
